import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type ToastLevel = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  level: ToastLevel;
  message: string;
}

interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const DEFAULT_TIMEOUT_MS = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const push = useCallback((level: ToastLevel, message: string) => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, level, message }]);
    // Les erreurs (souvent multi-lignes, à lire et corriger) restent plus
    // longtemps que les confirmations.
    const ttl = level === 'error' ? 9000 : DEFAULT_TIMEOUT_MS;
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, ttl);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (m) => push('success', m),
      error: (m) => push('error', m),
      info: (m) => push('info', m),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastStack toasts={toasts} onDismiss={(id) => setToasts((prev) => prev.filter((t) => t.id !== id))} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast doit être utilisé dans un <ToastProvider>');
  }
  return ctx;
}

// ─── UI ────────────────────────────────────────────────────────────────────

function ToastStack({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: number) => void;
}) {
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-full pointer-events-none">
      {toasts.map((t) => (
        <ToastView key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

const STYLES: Record<ToastLevel, string> = {
  success: 'bg-emerald-600 text-white ring-emerald-700',
  error: 'bg-red-600 text-white ring-red-700',
  info: 'bg-slate-800 text-white ring-slate-700',
};

const ICONS: Record<ToastLevel, string> = {
  success: '✓',
  error: '✕',
  info: 'ⓘ',
};

function ToastView({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  return (
    <div
      role="status"
      className={`pointer-events-auto rounded-md shadow-lg ring-1 px-4 py-2.5 text-sm flex items-start gap-3 ${STYLES[toast.level]}`}
    >
      <span aria-hidden className="font-bold leading-5">
        {ICONS[toast.level]}
      </span>
      <p className="flex-1 leading-5 whitespace-pre-line">{toast.message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Fermer"
        className="opacity-70 hover:opacity-100 leading-5"
      >
        ×
      </button>
    </div>
  );
}
